import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';

import {createFolder as createFolderAction} from '../../actions/application';
import { setUserFolders } from '../../actions/lexon';
import { setIsUpdatedFolder } from '../../actions/folders';
import { getUserFolders } from '../../services/accounts';
import Spinner from '../spinner/spinner';
import FolderCreateDialog from './folder-create-dialog';
import FolderRenameDialog from './folder-rename-dialog';
import FolderList from './folder-list';
import '../../styles/main.scss';
import './folder-container.scss';

export const FolderContainer = ({
  activeRequests,
  folderTree,
  folders,
  collapsed,
  lexon,
  email,
  updateUserFolders
}) => {
  const initialListLoading = activeRequests > 0 && folderTree.length === 0;
  const [localFolderTree, setLocalFolderTree] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const addOrderToFolders = orderFolders => orderFolders.map((folder, idx) => {
    const folderWithOrder = { ...folder, order: idx + 1 };
    if (Array.isArray(folder.children) && folder.children.length > 0) {
      folderWithOrder.children = addOrderToFolders(folder.children);
    }
    return folderWithOrder;
  });

  const getUniqueOrder = existingOrders => {
    let nextOrder = 1;
    while (existingOrders.has(nextOrder)) {
      nextOrder++;
    }
    return nextOrder;
  };

  const addOrderToChildren = (children, existingOrders, userFolders) =>
    children.map(child => {
      const userFolder = userFolders.find(f => f.name === child.folderId);
      let order;

      if (userFolder) {
        order = userFolder.order;
      } else {
        if (!order || existingOrders.has(order)) {
          order = getUniqueOrder(existingOrders);
        }
        existingOrders.add(order);
      }

      const childWithOrder = { ...child, order };
      if (Array.isArray(child.children) && child.children.length > 0) {
        childWithOrder.children = addOrderToChildren(child.children, existingOrders, userFolders);
      }
      return childWithOrder;
    });

  const loadUserFolders = async () => {
    // Si no hay folderTree, no hay nada que procesar
    if (!folderTree || folderTree.length === 0) {
      return;
    }

    // Si no hay usuario o email, al menos mostramos los folders con orden por defecto
    if(!lexon.userId || !email) {
      const orderedFolders = addOrderToFolders(folderTree);
      setLocalFolderTree(orderedFolders);
      return;
    }

    try {
      setIsLoading(true);
      const resUserFolders = await getUserFolders(lexon.userId, email, lexon.lexonToken);
      if (resUserFolders.data && resUserFolders.data.length > 0) {
        updateUserFolders(resUserFolders.data);
        setIsLoading(false);
        const existingOrders = new Set(resUserFolders.data.map(f => f.order));
        const updatedFolderTree = folderTree.map(folder => {
          const userFolder = resUserFolders.data.find(f => f.name === folder.folderId);
          let order;
          if (userFolder) {
            order = userFolder.order;
          } else {
            order = folder.order;
            if (!order || existingOrders.has(order)) {
              order = getUniqueOrder(existingOrders);
            }
          }
          existingOrders.add(order);
          const folderWithOrder = { ...folder, order };
          if (Array.isArray(folder.children) && folder.children.length > 0) {
            folderWithOrder.children = addOrderToChildren(
              folder.children,
              existingOrders,
              resUserFolders.data
            );
          }
          return folderWithOrder;
        });
        setLocalFolderTree(updatedFolderTree);
      } else {
        const orderedFolders = addOrderToFolders(folderTree);
        setLocalFolderTree(orderedFolders);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error loading user folders:', error);
      const orderedFolders = addOrderToFolders(folderTree);
      setLocalFolderTree(orderedFolders);
      setIsLoading(false);
    }
  };

  // FOLDERS_SET replaces `folderTree` with a fresh array/object identity on every
  // background poll tick, even when the folder set itself hasn't changed. Depending
  // on that identity would re-run loadUserFolders() (and its Spinner) on every poll,
  // so re-run only when the actual set of folder ids changes.
  const folderIdsKey = useMemo(
    () => (folderTree || []).map(folder => folder.folderId).join(','),
    [folderTree]
  );

  useEffect(() => {
    loadUserFolders();
  }, [folderIdsKey, lexon.userId, lexon.lexonToken, email]);

  return (
    <nav className={`mdc-list folder-scroll-container`}>
      <Spinner visible={initialListLoading || isLoading}
        canvasClassName={`spinnerCanvas`}
      />
      <FolderList
        folderTree={localFolderTree}
        folders={folders}
        toggle={true}
        collapsed={collapsed}
      />
      <FolderCreateDialog />
      <FolderRenameDialog />
    </nav>
  );
};

FolderContainer.propTypes = {
  activeRequests: PropTypes.number,
  folderTree: PropTypes.array,
  folders: PropTypes.object,
  createFolder: PropTypes.func
};

const mapStateToProps = state => ({
  activeRequests: state.folders.activeRequests,
  folderTree: state.folders.items,
  lexon: state.lexon,
  email: state.login.formValues.user,
  folders: state.folders.explodedItems,
  isUpdatedFolder: state.folders.isUpdatedFolder
});

const mapDispatchToProps = dispatch => ({
  createFolder: () => dispatch(createFolderAction('')),
  updateFolderStatus: isUpdatedFolder => dispatch(setIsUpdatedFolder(isUpdatedFolder)),
  updateUserFolders: folders => dispatch(setUserFolders(folders))
});

export default connect(mapStateToProps, mapDispatchToProps)(FolderContainer);
